// Column resize: the widths a columns block's resize handle writes. Donor: the Lexical view's BlockActionsPlugin column
// resize handle (Arrow keys step, pointer drag), whose RESIZE_COLUMNS_COMMAND now writes through this rule for every view.
import type { Block, Blocks } from '@w/content-blocks/src/block'
import { setBlockProps } from './ops'
import type { CoreState } from './state'
import { children } from './tree-read'

/** The narrowest a column gets, in percent of the columns block. */
export const COLUMN_MIN_WIDTH = 15
/** The boundary move of one ArrowLeft/ArrowRight on a focused resize handle, in percent. */
export const COLUMN_KEY_STEP = 5

/** The widths (percent) of two adjacent columns. */
export interface ColumnPair { leftWidth: number; rightWidth: number }

/**
 * A column's width in percent: a number as is, a `"50%"` string (the markdown attribute) parsed; null for any other
 * value, which the view measures from the rendered columns instead.
 */
export function columnPercent(width: unknown): number | null {
  const value = typeof width === 'number' ? width : typeof width === 'string' && /^\s*[\d.]+%?\s*$/.test(width) ? Number(width.trim().replace(/%$/u, '')) : Number.NaN
  return Number.isFinite(value) && value > 0 ? value : null
}

/** The pair with its boundary at `leftWidth`, each column kept at least COLUMN_MIN_WIDTH wide; the pair total is kept. */
export function moveColumnBoundary(pair: ColumnPair, leftWidth: number): ColumnPair {
  const total = pair.leftWidth + pair.rightWidth
  const left = Math.min(Math.max(leftWidth, COLUMN_MIN_WIDTH), total - COLUMN_MIN_WIDTH)
  return { leftWidth: left, rightWidth: total - left }
}

/** The pair after an ArrowLeft (`-1`) or ArrowRight (`1`) on its resize handle. */
export function keyResizeColumns(pair: ColumnPair, direction: -1 | 1): ColumnPair {
  return moveColumnBoundary(pair, pair.leftWidth + direction * COLUMN_KEY_STEP)
}

/**
 * The pair `start` after a pointer drag of `deltaPx` across the two columns' `availablePx` rendered width, rounded to a
 * tenth of a percent as the drag's release writes it.
 */
export function dragResizeColumns(start: ColumnPair, deltaPx: number, availablePx: number): ColumnPair {
  const moved = moveColumnBoundary(start, start.leftWidth + (deltaPx / availablePx) * (start.leftWidth + start.rightWidth))
  return { leftWidth: roundColumnWidth(moved.leftWidth), rightWidth: roundColumnWidth(moved.rightWidth) }
}

function roundColumnWidth(width: number): number {
  return Math.round(width * 10) / 10
}

/** The column `id` and the column after it in the same columns block, or undefined. */
export function adjacentColumns(blocks: Blocks, leftId: string, rightId: string): { left: Block; right: Block } | undefined {
  const visit = (items: Blocks): { left: Block; right: Block } | undefined => {
    for (const [index, block] of items.entries()) {
      const next = items[index + 1]
      if (block.id === leftId && next?.id === rightId && block.type === 'col' && next.type === 'col') return { left: block, right: next }
      const found = visit(children(block))
      if (found) return found
    }
    return undefined
  }
  return visit(blocks)
}

/** Writes the widths of column `leftId` and the column `rightId` after it; false when they are no adjacent columns. */
export function resizeColumns(state: CoreState, leftId: string, rightId: string, pair: ColumnPair): boolean {
  const found = adjacentColumns(state.blocks, leftId, rightId)
  if (!found) return false
  setBlockProps(state, found.left, { width: pair.leftWidth })
  setBlockProps(state, found.right, { width: pair.rightWidth })
  return true
}
